<script setup lang="ts">
    import { cleanHtml } from '@/utils/string';
    import { computed, onMounted, ref } from 'vue';

    type InputMode = 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url';

    const inputRef = ref<HTMLInputElement>();

    type InputValue = string | number | null | undefined;

    const model = defineModel<InputValue>({
        set(value) {
            if (typeof value === 'string') {
                return cleanHtml(value);
            }

            return value;
        },
    });

    const {
        name,
        label,
        required = false,
        hidden = false,
        type = 'text',
        inputOptions = null,
        autocomplete = 'off',
        inputMode = undefined,
        error = undefined,
        placeholder = '',
        showLabel = true,
        ...props
    } = defineProps<{
        name: string;
        label: string;
        required?: boolean;
        disabled?: boolean;
        focus?: boolean;
        type?: 'text' | 'email' | 'password' | 'number';
        inputOptions?: Record<string, string> | null;
        autocomplete?: string;
        hidden?: boolean;
        inputMode?: InputMode;
        placeholder?: string;
        showLabel?: boolean;
        error?: string;
        customClass?: string[];
    }>();

    onMounted(() => {
        if (inputRef.value && props.focus) {
            inputRef.value.focus();
        }
    });

    const inputModeComputed = computed<InputMode>(() => {
        // Override input mode if it's explicitly set
        if (inputMode !== undefined) {
            return inputMode;
        }

        // Otherwise, set it based on the type
        if (type === 'email') {
            return 'email';
        }
        if (type === 'number') {
            return 'numeric';
        }

        return 'text';
    });

    function onInput(event: Event): void {
        const rawValue = (event.target as HTMLInputElement)?.value;

        if (rawValue === '') {
            model.value = null;
            return;
        }
        model.value = type === 'number' ? Number(rawValue) : rawValue;
    }
</script>

<template>
    <div :class="[customClass]">
        <div
            :class="[
                hidden && 'hidden',
                disabled && 'cursor-not-allowed bg-gray-100 text-gray-400',
                !!error && 'border-red-600',
                'relative p-1',
            ]"
        >
            <label
                v-show="showLabel"
                :for="name"
                class="absolute -top-2 left-2 -mt-px inline-block bg-white px-1 text-xs"
            >
                {{ label }}
                <span
                    v-if="required"
                    class="text-red-600"
                    aria-hidden="true"
                    >*</span
                >
            </label>
            <div class="flex">
                <input
                    :id="name"
                    ref="inputRef"
                    :name
                    :value="modelValue"
                    :disabled
                    :type
                    :required
                    class="block w-full flex-1 cursor-text appearance-none border-0 border-border-primary px-1 py-0 text-sm text-text placeholder-text focus:outline-none disabled:cursor-not-allowed"
                    v-bind="inputOptions"
                    :inputmode="inputModeComputed"
                    :autocomplete
                    :placeholder
                    :aria-required="required"
                    :aria-invalid="!!error"
                    :aria-describedBy="`${name}-error`"
                    autocorrect="off"
                    autocapitalize="off"
                    @input="onInput"
                />

                <slot :input-ref />
            </div>
        </div>
        <span
            v-if="!!error"
            :id="`${name}-error`"
            :data-test="`${name}-error`"
            class="font-ro-regular text-red-500 mt-2 text-xs"
            >{{ error }}</span
        >
    </div>
</template>
